import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Button, Box } from '@mui/material';
import { styled } from '@mui/system';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getApps, initializeApp } from 'firebase/app';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { RadioGroup, FormControlLabel, Radio } from '@mui/material';

// Initialize Firebase
const firebaseConfig = {
    // Your Firebase configuration
};

// Check if a Firebase app is already initialized
const app = !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];
const db = getFirestore(app);

// Styled components using MUI's styled API
const StyledCard = styled(Card)({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.3s, box-shadow 0.3s',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
    background: 'linear-gradient(135deg, #f5f7fa, #c3cfe2)',
    borderRadius: '12px',
});

const StyledButton = styled(Button)({
    margin: '10px',
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    color: 'white',
    '&:hover': {
        background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
    },
});

const TilePage = () => {
    const [websites, setWebsites] = useState([]);
    const [openCincinnatiPopup, setOpenCincinnatiPopup] = useState(false);
    const [openLamsPopup, setOpenLamsPopup] = useState(false);
    const [openLamsQuestionsPopup, setOpenLamsQuestionsPopup] = useState(false);
    
    // State for Cincinnati questions
    const [facialDroop, setFacialDroop] = useState('');
    const [armDrift, setArmDrift] = useState('');
    const [speechSlurred, setSpeechSlurred] = useState('');
    const [lamsScore, setLamsScore] = useState(null);

    // State for LAMS questions
    const [faceScore, setFaceScore] = useState(0);
    const [armScore, setArmScore] = useState(0);
    const [gripScore, setGripScore] = useState(0);

    const handleCincinnatiPopupOpen = () => setOpenCincinnatiPopup(true);
    const handleCincinnatiPopupClose = () => {
        setFacialDroop('');
        setArmDrift('');
        setSpeechSlurred('');
        setOpenCincinnatiPopup(false);
    };

    const handleLamsPopupClose = () => {
        setLamsScore(null);
        setOpenLamsPopup(false);
    };

    const handleLamsQuestionsPopupClose = () => {
        setFaceScore(0);
        setArmScore(0);
        setGripScore(0);
        setOpenLamsQuestionsPopup(false);
    };

    const handleSubmitCincinnati = () => {
        if (facialDroop === 'yes' || armDrift === 'yes' || speechSlurred === 'yes') {
            handleCincinnatiPopupClose();
            setOpenLamsQuestionsPopup(true); // Open LAMS questions dialog
        } else {
            alert("No signs of stroke detected.");
            handleCincinnatiPopupClose();
        }
    };

    const handleSubmitLams = () => {
        const totalScore = faceScore + armScore + gripScore;
        setLamsScore(totalScore);
        handleLamsQuestionsPopupClose();
        setOpenLamsPopup(true); // Open LAMS score dialog
    };

    useEffect(() => {
        const fetchTiles = async () => {
            const tilesCollection = collection(db, 'hvac/GenericData/tiles');
            const tilesSnapshot = await getDocs(tilesCollection);
            const tilesList = tilesSnapshot.docs.map(doc => ({
                Name: doc.data().Name,
                url: doc.data().url,
                description: doc.data().Description,
            }));
            setWebsites(tilesList);
        };

        fetchTiles();
    }, []);

    return (
        <Box sx={{ padding: '20px' }}>
            <Grid container spacing={3}>
                {websites.map((site, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <StyledCard>
                            <CardContent>
                                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                                    {site.Name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ marginTop: '10px' }}>
                                    {site.description}
                                </Typography>
                            </CardContent>
                            <StyledButton
                                component="a"
                                href={site.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Go To {site.Name}
                            </StyledButton>
                        </StyledCard>
                    </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                                Cincinnati and LAMS Score
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ marginTop: '10px' }}>
                                Click the button below to determine the scores.
                            </Typography>
                        </CardContent>
                        <StyledButton onClick={handleCincinnatiPopupOpen}>
                            Show Scores
                        </StyledButton>
                    </StyledCard>
                </Grid>
            </Grid>

            {/* First Popup for Cincinnati Assessment */}
            <Dialog open={openCincinnatiPopup} onClose={handleCincinnatiPopupClose}>
                <DialogTitle>Cincinnati Assessment</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">Facial Droop:</Typography>
                    <RadioGroup value={facialDroop} onChange={(e) => setFacialDroop(e.target.value)}>
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>

                    <Typography variant="body1">Arm Drift:</Typography>
                    <RadioGroup value={armDrift} onChange={(e) => setArmDrift(e.target.value)}>
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>

                    <Typography variant="body1">Speech Slurred:</Typography>
                    <RadioGroup value={speechSlurred} onChange={(e) => setSpeechSlurred(e.target.value)}>
                        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmitCincinnati} color="primary">
                        Submit
                    </Button>
                    <Button onClick={handleCincinnatiPopupClose} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Popup for LAMS Questions */}
            <Dialog open={openLamsQuestionsPopup} onClose={handleLamsQuestionsPopupClose}>
                <DialogTitle>LAMS Questions</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">Face:</Typography>
                    <RadioGroup value={faceScore} onChange={(e) => setFaceScore(Number(e.target.value))}>
                        <FormControlLabel value={0} control={<Radio />} label="0: Both sides move normally" />
                        <FormControlLabel value={1} control={<Radio />} label="1: One side is weak or flaccid" />
                    </RadioGroup>

                    <Typography variant="body1">Arm:</Typography>
                    <RadioGroup value={armScore} onChange={(e) => setArmScore(Number(e.target.value))}>
                        <FormControlLabel value={0} control={<Radio />} label="0: Both sides move normally" />
                        <FormControlLabel value={1} control={<Radio />} label="1: One side is weak" />
                        <FormControlLabel value={2} control={<Radio />} label="2: One side is flaccid/doesn't move" />
                    </RadioGroup>

                    <Typography variant="body1">Grip:</Typography>
                    <RadioGroup value={gripScore} onChange={(e) => setGripScore(Number(e.target.value))}>
                        <FormControlLabel value={0} control={<Radio />} label="0: Both sides move normally" />
                        <FormControlLabel value={1} control={<Radio />} label="1: One side is weak" />
                        <FormControlLabel value={2} control={<Radio />} label="2: One side is flaccid/doesn't move" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmitLams} color="primary">
                        Submit
                    </Button>
                    <Button onClick={handleLamsQuestionsPopupClose} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Second Popup for LAMS Score */}
            <Dialog open={openLamsPopup} onClose={handleLamsPopupClose}>
                <DialogTitle>LAMS Score</DialogTitle>
                <DialogContent>
                    {lamsScore !== null && (
                        <Typography variant="body1">LAMS Score: {lamsScore}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleLamsPopupClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TilePage;
